import React from 'react';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import { type Control } from 'react-hook-form';
import { useTranslation } from 'src/arb-wrapper';
import { FormSelect } from 'src/components/common/forms/FormSelect';

export interface ContentRuleOption {
  label: string;
  value: string;
}

interface ContentRuleSelectorProps {
  /** react-hook-form control from the enclosing campaign form. */
  control: Control;
  /** Disabled until the campaign has a publisher and business line (create mode). */
  disabled: boolean;
  /** Whether the applicable-rules query is in flight. */
  loading: boolean;
  /** Selectable rules (applicable ∪ attached, deduped by the caller). */
  options: ContentRuleOption[];
  /** Currently selected rule ids (watched form value). */
  selectedIds: string[];
  /** Removes one rule id from the selection (chip close). */
  onRemove: (id: string) => void;
}

/**
 * Editable content-rule multi-select with removable chips, shared by the campaign create and
 * edit forms. Selected rules render as chips absolutely positioned below the select so the
 * surrounding form layout never shifts as rules are added or removed. Purely presentational:
 * the queries, flag gating, and save-merge live in the enclosing form.
 */
export const ContentRuleSelector: React.FC<ContentRuleSelectorProps> = ({ control, disabled, loading, options, selectedIds, onRemove }) => {
  const { t } = useTranslation();

  return (
    <div style={{ position: 'relative' }}>
      <FormSelect
        control={control}
        data-testid="campaign-edit-content-rules-select"
        disabled={disabled}
        label={t('form_campaigns_content_rules')}
        loading={loading}
        name="contentRuleConfigIds"
        options={options}
      />
      {selectedIds.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, paddingTop: '8px', zIndex: 1 }}>
          <Row alignmentVertical="center" data-testid="campaign-edit-content-rules-selected" spacing="200" wrap="down">
            {selectedIds.map((id) => (
              <Tag
                data-testid={`campaign-edit-content-rule-tag-${id}`}
                key={id}
                type="theme"
                onClose={() => { onRemove(id); }}
              >
                {options.find((o) => o.value === id)?.label ?? id}
              </Tag>
            ))}
          </Row>
        </div>
      )}
    </div>
  );
};
